// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { setupAxiosInterceptors, getAuthTokenKey } from "../utils/axiosInstance";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(getAuthTokenKey());
    if (token) {
      setIsAuthenticated(true);
      storeUserData(token);
    } else {
      setIsAuthenticated(false);
    }
    setLoading(false);

    setupAxiosInterceptors(logout);

  }, []);

  const login = (token) => {
    localStorage.setItem(getAuthTokenKey(), token);
    setIsAuthenticated(true);
    
    try {
      const decodedToken = jwtDecode(token);
      setRole(decodedToken.role);
      setUserDetail(decodedToken);
      
      if (decodedToken.role === "Admin") {
        navigate("/admin/dashboard");
      } else if (decodedToken.role === "Applicant") {
        if (decodedToken.mustChangePassword) {
          navigate("/portal/change-password");
        } else {
          navigate("/portal/dashboard");
        }
      } else if (decodedToken.role === "Community") {
        navigate("/community/applications");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Invalid token on login", error);
      setIsAuthenticated(false);
      navigate("/login");
    }
  };

  const logout = () => {
    const isApplicant = role === "Applicant";
    localStorage.removeItem(getAuthTokenKey());
    setIsAuthenticated(false);
    setRole(null);
    setUserDetail(null);
    if (isApplicant) {
      navigate("/portal/login");
    } else {
      navigate("/login");
    }
  };

  const storeUserData = (token) => {
    try {
      const decodedToken = jwtDecode(token);
      setRole(decodedToken.role);
      setUserDetail(decodedToken);
    } catch (error) {
      console.error("Invalid token", error);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, login, logout, role, userDetail }}>
      {children}
    </AuthContext.Provider>
  );
};
